// src/components/IntroScrollLogo.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

interface IntroScrollLogoProps {
  scrollY: number;
  maxScroll?: number;
  // ✅ NUEVO: callback que App.tsx usa para saber cuándo desmontar el overlay
  onComplete?: () => void;
}

const INTRO_DURATION_MS = 900;

const IntroScrollLogo: React.FC<IntroScrollLogoProps> = ({
  scrollY,
  maxScroll = 400,
  onComplete,
}) => {
  /* ============================================================================
   * 🧠 Scroll progress (del usuario)
   * ========================================================================== */
  const clamped = Math.min(scrollY, maxScroll);
  const progress = clamped / maxScroll;

  /* ============================================================================
   * 📱 Mobile detection (solo layout/sizes)
   * ========================================================================== */
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ============================================================================
   * 🖱️ Mouse tilt (3D)
   * ========================================================================== */
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window;
      const normX = (e.clientX / innerWidth) * 2 - 1;
      const normY = (e.clientY / innerHeight) * 2 - 1;
      const maxTilt = 16;
      setTilt({ x: -normY * maxTilt, y: normX * maxTilt });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const tiltStrong = useMemo(
    () => ({ x: tilt.x * 1.3, y: tilt.y * 1.3 }),
    [tilt],
  );

  /* ============================================================================
   * 🎬 Intro animation (autoplay)
   * ========================================================================== */
  const [introProgress, setIntroProgress] = useState(0);
  const [introAnimating, setIntroAnimating] = useState(true);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const elapsed = timestamp - start;
      const t = Math.min(1, elapsed / INTRO_DURATION_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      setIntroProgress(eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setIntroAnimating(false);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  /* ============================================================================
   * 🔒 Bloqueo de scroll mientras corre la animación de intro
   * ========================================================================== */
  useEffect(() => {
    if (!introAnimating) return;

    const getScrollContainer = (): HTMLElement | null => {
      const el = document.querySelector<HTMLElement>(
        ".h-screen.overflow-y-scroll",
      );
      return el ?? (document.scrollingElement as HTMLElement | null);
    };

    const container = getScrollContainer();

    const prevent = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY > 0) prevent(e);
    };
    const onTouchStart = (e: TouchEvent) => {
      (onTouchStart as any)._y = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const startY = (onTouchStart as any)._y ?? 0;
      const delta = startY - (e.touches[0]?.clientY ?? 0);
      if (delta > 0) prevent(e);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (["ArrowDown", "PageDown", "End", " ", "Spacebar"].includes(e.key))
        prevent(e);
    };

    window.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchstart", onTouchStart, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true,
    });
    window.addEventListener("keydown", onKeyDown, { capture: true });
    if (container) container.scrollTop = 0;

    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true } as any);
      window.removeEventListener("touchstart", onTouchStart, {
        capture: true,
      } as any);
      window.removeEventListener("touchmove", onTouchMove, {
        capture: true,
      } as any);
      window.removeEventListener("keydown", onKeyDown, {
        capture: true,
      } as any);
    };
  }, [introAnimating]);

  /* ============================================================================
   * ✅ NUEVO: notificar a App cuando el overlay terminó de desvanecerse.
   * Se dispara cuando progress llega a 1 (opacity → 0) para que App
   * desmonte el componente DESPUÉS de que el CSS transition de 0.18s termine,
   * evitando cualquier flash.
   * ========================================================================== */
  const onCompleteCalledRef = useRef(false);

  useEffect(() => {
    if (progress >= 1 && !onCompleteCalledRef.current) {
      onCompleteCalledRef.current = true;
      // Esperar a que termine el transition de opacity (180ms) antes de desmontar
      const tid = setTimeout(() => {
        onComplete?.();
      }, 200);
      return () => clearTimeout(tid);
    }
  }, [progress, onComplete]);

  /* ============================================================================
   * 🧩 Motion values (logos + bg)
   * ========================================================================== */
  const logoTravel = isMobile ? 150 : 200;
  const translateYLogo1 = -progress * logoTravel;

  const baseScaleLogo1 = isMobile ? 1.25 : 1.9;
  const scaleDropLogo1 = isMobile ? 0.35 : 0.6;
  const scaleLogo1 = baseScaleLogo1 - progress * scaleDropLogo1;

  const fadeStart = 0.6;
  let opacity = 1;
  if (progress > fadeStart) {
    const localProgress = (progress - fadeStart) / (1 - fadeStart);
    opacity = 1 - localProgress;
  }

  const maxRot = 14;
  const dynamicAngle = 155 + progress * maxRot;
  const bgTranslateY = -progress * (isMobile ? 110 : 150);
  const maxDarkOpacity = 0.45;
  const bgDarkOpacity = Math.min(progress * maxDarkOpacity, maxDarkOpacity);

  const introOffsetMax = isMobile ? 140 : 220;
  const introOffsetLogo1 = (1 - introProgress) * -introOffsetMax;
  const introOpacity = introProgress;

  const hintFadeStart = 0.05;
  const hintFadeEnd = 0.25;
  let hintScrollOpacity = 1;
  if (progress > hintFadeStart) {
    const local =
      (progress - hintFadeStart) / (hintFadeEnd - hintFadeStart || 1);
    hintScrollOpacity = Math.max(0, 1 - Math.min(local, 1));
  }
  const hintOpacity = introOpacity * hintScrollOpacity;

  const logoMaxWClass = isMobile ? "max-w-[16rem]" : "max-w-[25rem]";

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none overflow-hidden"
      style={{
        opacity,
        transition: "opacity 0.18s linear",
        perspective: "1200px",
      }}
    >
      {/* Fondo */}
      <div
        className="absolute inset-0 -z-20"
        style={{
          backgroundImage: `
            linear-gradient(${dynamicAngle}deg, #202020 20%, #111111 50%, transparent 50%),
            linear-gradient(${dynamicAngle}deg, #101010 50%, transparent 70%)
          `,
          backgroundBlendMode: "normal",
          backgroundSize: "140% 140%",
          backgroundPosition: "center center",
          transform: `translateY(${bgTranslateY}px)`,
          willChange: "transform",
          transition: "transform 0.08s ease-out, background-image 0.12s linear",
        }}
      />

      {/* Oscurecido */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          backgroundColor: `rgba(0, 0, 0, ${bgDarkOpacity})`,
          transition: "background-color 0.12s linear",
        }}
      />

      {/* Logo 1 */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          opacity: introOpacity,
          transform: `
            translate(-50%, -50%)
            translateX(${introOffsetLogo1}px)
            translateY(${translateYLogo1}px)
            scale(${scaleLogo1})
            rotateX(${tiltStrong.x}deg)
            rotateY(${tiltStrong.y}deg)
          `,
          transformStyle: "preserve-3d",
          transition: "transform 0.05s linear",
        }}
      >
        <img
          loading="lazy"
          decoding="async"
          src={"https://res.cloudinary.com/xvzxvfee/image/upload/v1788743202/SprintCoreLogo7_mz94ox.webp"}
          alt="SprintCore"
          className={`${logoMaxWClass} h-auto`}
        />
      </div>

      {/* Hint scroll */}
      <div
        className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{ opacity: hintOpacity, transition: "opacity 0.12s linear" }}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-gray-300/80">
            Desliza
          </span>
          <ChevronDown className="text-white text-2xl arrow-bounce" />
        </div>
      </div>

      <style>{`
        @keyframes arrow-bounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50%      { transform: translateY(6px); opacity: 0.8; }
        }
        .arrow-bounce { animation: arrow-bounce 1.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default IntroScrollLogo;
